import React, {useState, useEffect} from 'react';
import {
    Box,
    Divider,
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    InputLabel
} from '@mui/material';
import PropTypes from 'prop-types';

const Separator = ({
                       field,
                       isEditMode,
                       submitMode,
                       onChange,
                       index,
                   }) => {
    const [currentDescription, setCurrentDescription] = useState(field.description || '');

    useEffect(() => {
        setCurrentDescription(field.description || '');
    }, [field.description]);

    const hasText = currentDescription && currentDescription.trim() !== '';
    const alignment = field.alignment || 'center';

    const renderDivider = () => {
        if (!hasText) {
            return (
                <Box sx={{py: 4}}>
                    <Divider/>
                </Box>
            );
        }

        return (
            <Box sx={{
                py: 2,
                width: '100%'
            }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent:
                            alignment === 'left'
                                ? 'flex-start'
                                : alignment === 'right'
                                    ? 'flex-end'
                                    : 'center',
                        width: '100%',
                    }}
                >
                    {alignment !== 'left' && (
                        <Box sx={{
                            flex: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                        }}/>
                    )}

                    <Typography
                        variant="subtitle2"
                        color="textSecondary"
                        sx={{
                            mx: 2,
                            fontWeight: 800,
                            fontSize: 20,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            textAlign: alignment,
                            flexShrink: 0,
                            maxWidth: '100%',
                        }}
                    >
                        {currentDescription}
                    </Typography>

                    {alignment !== 'right' && (
                        <Box sx={{
                            flex: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider'
                        }}/>
                    )}
                </Box>
            </Box>
        );
    };

    if (submitMode) {
        return renderDivider();
    }

    if (isEditMode) {
        return (
            <Box sx={{my: 2}}>
                <Typography variant="body2" color="textSecondary" sx={{mb: 1}}>
                    {field.label || 'Separator'}
                </Typography>

                <Box sx={{mb: 2}}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Separator Text (optional)"
                        value={currentDescription}
                        onChange={e => {
                            const value = e.target.value;
                            if (value.length <= 80) {
                                setCurrentDescription(value);
                                onChange({
                                    index,
                                    description: value,
                                });
                            }
                        }}
                        placeholder="Leave empty for a simple line"
                        helperText={`Add text to create a divider with text (${currentDescription.length}/80 characters)`}
                        inputProps={{maxLength: 80}}
                    />
                </Box>

                {hasText && (
                    <Box sx={{mb: 2}}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Text Alignment</InputLabel>
                            <Select
                                value={alignment}
                                onChange={e =>
                                    onChange({
                                        index,
                                        alignment: e.target.value,
                                    })
                                }
                                label="Text Alignment"
                            >
                                <MenuItem value="left">Left</MenuItem>
                                <MenuItem value="center">Center</MenuItem>
                                <MenuItem value="right">Right</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}

                <Box sx={{
                    mt: 2,
                    p: 2,
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 1
                }}>
                    <Typography variant="caption" color="textSecondary" sx={{
                        mb: 1,
                        display: 'block'
                    }}>
                        Preview:
                    </Typography>
                    {renderDivider()}
                </Box>
            </Box>
        );
    }

    return renderDivider();
};

Separator.propTypes = {
    field: PropTypes.object.isRequired,
    isEditMode: PropTypes.bool,
    submitMode: PropTypes.bool,
    readOnly: PropTypes.bool,
    onChange: PropTypes.func,
    index: PropTypes.number,
};

export default Separator;
